import React, { useState, useEffect } from 'react';
import { Card, Col, Container, Row, Table, Modal, Button, Form, Dropdown, Badge } from 'react-bootstrap';
import BreadCrumb from 'Common/BreadCrumb';
import Swal from 'sweetalert2';
import axios from 'axios';

interface JobPosting {
  id: number;
  nama: string;
  flatform: string;
  link: string | null;
  qr_code: string | null;
  status: string;
  applicant_count?: number;
  qualification_count?: number;
  iq_average?: number;
  eq_average?: number;
  value_average?: number;
  created_at: string;
  updated_at: string;
}

const JobPosting = () => {
  document.title = 'Job Posting | CITY SERVICE';

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    nama: '',
    flatform: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState<JobPosting[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [platformOptions, setPlatformOptions] = useState<{id: string; flatform_name: string}[]>([]);
  const [showPlatformModal, setShowPlatformModal] = useState(false);
  const [newPlatformName, setNewPlatformName] = useState('');

  const statusOptions = [
    { value: '0', label: 'Draft', color: 'secondary' },
    { value: '1', label: 'Active', color: 'success' },
    { value: '2', label: 'Paused', color: 'warning' },
    { value: '3', label: 'Closed', color: 'danger' }
  ];

  const getStatusInfo = (status: string) => {
    return statusOptions.find(option => option.value === status) || statusOptions[0];
  };

  const handleShowModal = () => {
    setEditMode(false);
    setEditId(null);
    setFormData({
      nama: '',
      flatform: '',
    });
    fetchPlatformOptions(); // Refresh platform options
    setShowModal(true);
  };

  const handleShowEditModal = (jobPosting: JobPosting) => {
    setEditMode(true);
    setEditId(jobPosting.id);
    setFormData({
      nama: jobPosting.nama,
      flatform: jobPosting.flatform,
    });
    fetchPlatformOptions(); // Refresh platform options
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setEditId(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { id, value } = e.target;
    setFormData({ 
      ...formData, 
      [id]: value 
    });
  };

  const handleFormSubmit = async () => {
    if (!formData.nama.trim() || !formData.flatform.trim()) {
      Swal.fire('Error', 'Please fill in all required fields (Name and Platform).', 'error');
      return;
    }

    setIsLoading(true);
    Swal.fire({
      title: editMode ? 'Updating...' : 'Saving...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token is missing. Please log in again.');
      }

      const requestData = {
        nama: formData.nama,
        flatform: formData.flatform,
      };

      let response;
      if (editMode && editId) {
        response = await axios.put(
          `/master-jobpost/${editId}`,
          requestData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } else {
        response = await axios.post(
          '/master-jobpost',
          requestData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }

      Swal.fire(
        'Success', 
        `Job Posting ${editMode ? 'updated' : 'added'} successfully!`, 
        'success'
      );
      setShowModal(false);
      setFormData({ 
        nama: '',
        flatform: '',
      });
      fetchJobPostings();
    } catch (error) {
      Swal.fire(
        'Error',
        (axios.isAxiosError(error) && error.response?.data?.message) || 
        (error instanceof Error && error.message) || 
        'An unknown error occurred',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPlatformOptions = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token is missing. Please log in again.');
      }

      const response = await axios.get('/master-flatform', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setPlatformOptions(response.data.data || []);
    } catch (error) {
      console.error('Error fetching platform options:', error);
      // Set empty array as fallback
      setPlatformOptions([]);
    }
  };

  const handleAddPlatform = () => {
    setNewPlatformName('');
    setShowPlatformModal(true);
  };

  const handleClosePlatformModal = () => {
    setShowPlatformModal(false);
    setNewPlatformName('');
  };

  const handlePlatformNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewPlatformName(e.target.value);
  };

  const validatePlatformName = () => {
    if (!newPlatformName.trim()) {
      return 'Platform name is required';
    }
    if (newPlatformName.trim().length < 2) {
      return 'Platform name must be at least 2 characters';
    }
    const exists = platformOptions.some(
      option => option.flatform_name.toLowerCase() === newPlatformName.trim().toLowerCase()
    );
    if (exists) {
      return 'This platform already exists';
    }
    return null;
  };

  const handleSubmitNewPlatform = async () => {
    const validationError = validatePlatformName();
    if (validationError) {
      Swal.fire('Validation Error', validationError, 'error');
      return;
    }

    setIsLoading(true);

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token is missing. Please log in again.');
      }

      const response = await axios.post(
        '/master-flatform',
        { flatform_name: newPlatformName.trim() },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Refresh platform options to include the new one
      await fetchPlatformOptions();

      // Auto-select the newly added platform
      setFormData(prev => ({
        ...prev,
        flatform: newPlatformName.trim()
      }));

      // Close modal and show success
      handleClosePlatformModal();
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: `Platform "${newPlatformName.trim()}" has been added successfully.`,
        showConfirmButton: false,
        timer: 1500
      });

    } catch (error) {
      Swal.fire(
        'Error',
        (axios.isAxiosError(error) && error.response?.data?.message) || 
        (error instanceof Error && error.message) || 
        'Failed to add new platform',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchJobPostings = async () => {
    setIsLoading(true);
    Swal.fire({
      title: 'Loading...',
      html: 'Please wait while we fetch the data.',
      timerProgressBar: true,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token is missing. Please log in again.');
      }

      const response = await axios.get('/master-jobpost', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setTableData(response.data.data || []);
      Swal.fire({
        icon: 'success',
        title: 'Data Loaded',
        showConfirmButton: false,
        timer: 1000,
      });
    } catch (error) {
      Swal.fire(
        'Error',
        (axios.isAxiosError(error) && error.response?.data?.message) || 
        (error instanceof Error && error.message) || 
        'Failed to fetch job postings',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You won\'t be able to revert this!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsLoading(true);
        try {
          const token = localStorage.getItem('authToken');
          if (!token) {
            throw new Error('Authentication token is missing. Please log in again.');
          }

          await axios.delete(`/master-jobpost/${id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          Swal.fire('Deleted!', 'The job posting has been deleted.', 'success');
          setTableData((prevData) => prevData.filter((item) => item.id !== id));
        } catch (error) {
          Swal.fire(
            'Error',
            (axios.isAxiosError(error) && error.response?.data?.message) || 
            (error instanceof Error && error.message) || 
            'Failed to delete job posting',
            'error'
          );
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  const handleToggleStatus = async (id: number, currentStatus: string) => {
    setIsLoading(true);
    Swal.fire({
      title: 'Updating status...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token is missing. Please log in again.');
      }

      // Toggle between active (1) and paused (2)
      const newStatus = currentStatus === '1' ? '2' : '1';
      await axios.patch(
        `/master-jobpost/${id}`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setTableData((prevData) =>
        prevData.map((item) =>
          item.id === id ? { ...item, status: newStatus } : item
        )
      );
      Swal.fire('Success', 'Job posting status has been updated.', 'success');
    } catch (error) {
      Swal.fire(
        'Error',
        (axios.isAxiosError(error) && error.response?.data?.message) || 
        (error instanceof Error && error.message) || 
        'Failed to update job posting status',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const generateQRCode = async (id: number, link: string) => {
    if (!link) {
      Swal.fire('Error', 'Link is required to generate QR code.', 'error');
      return;
    }

    setIsLoading(true);
    Swal.fire({
      title: 'Generating QR Code...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token is missing. Please log in again.');
      }

      const response = await axios.post(
        `/master-jobpost/${id}/generate-qr`,
        { link },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setTableData((prevData) =>
        prevData.map((item) =>
          item.id === id ? { ...item, qr_code: response.data.qr_code } : item
        )
      );
      Swal.fire('Success', 'QR Code has been generated successfully.', 'success');
    } catch (error) {
      Swal.fire(
        'Error',
        (axios.isAxiosError(error) && error.response?.data?.message) || 
        (error instanceof Error && error.message) || 
        'Failed to generate QR code',
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPlatformOptions();
    fetchJobPostings();
  }, []);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb pageTitle="Job Posting" title="Job Posting" />

          <Row>
            <Col lg={12}>
              <Card>
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0">Job Posting Management</h5>
                    <div className="ms-auto">
                      <button
                        className="btn btn-dark d-flex align-items-center gap-1"
                        onClick={handleShowModal}
                        disabled={isLoading}
                      >
                        <i className="ri-add-line"></i>
                        <span>Create Job Posting</span>
                      </button>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>No</th>
                        <th>Name</th>
                        <th>Platform</th>
                        <th>QR Code</th>
                        <th>Applicant</th>
                        <th>Qualification</th>
                        <th>IQ</th>
                        <th>EQ</th>
                        <th>Value</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(tableData) && tableData.length > 0 ? (
                        tableData.map((jobPosting, index) => {
                          const statusInfo = getStatusInfo(jobPosting.status);
                          return (
                            <tr key={jobPosting.id}>
                              <td>{index + 1}</td>
                              <td>
                                <div style={{ maxWidth: '150px' }}>
                                  <strong>{jobPosting.nama}</strong>
                                </div>
                              </td>
                              <td>
                                <Badge bg="info">{jobPosting.flatform}</Badge>
                              </td>
                              <td>
                                {jobPosting.qr_code ? (
                                  <div className="d-flex align-items-center gap-2">
                                    <Badge bg="success" className="small">
                                      <i className="ri-qr-code-line me-1"></i>
                                      Ready
                                    </Badge>
                                  </div>
                                ) : (
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => generateQRCode(jobPosting.id, jobPosting.link || '')}
                                    disabled={isLoading || !jobPosting.link}
                                    title={!jobPosting.link ? 'Link required for QR code' : 'Generate QR Code'}
                                  >
                                    <i className="ri-qr-code-line me-1"></i>
                                    Generate
                                  </Button>
                                )}
                              </td>
                              <td>
                                <div className="text-center">
                                  <Badge bg="primary" className="fs-6">
                                    {jobPosting.applicant_count || 0}
                                  </Badge>
                                </div>
                              </td>
                              <td>
                                <div className="text-center">
                                  <Badge bg="secondary" className="fs-6">
                                    {jobPosting.qualification_count || 0}
                                  </Badge>
                                </div>
                              </td>
                              <td>
                                <div className="text-center">
                                  {jobPosting.iq_average ? (
                                    <Badge 
                                      bg={jobPosting.iq_average >= 80 ? 'success' : jobPosting.iq_average >= 60 ? 'warning' : 'danger'}
                                      className="fs-6"
                                    >
                                      {jobPosting.iq_average.toFixed(1)}
                                    </Badge>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <div className="text-center">
                                  {jobPosting.eq_average ? (
                                    <Badge 
                                      bg={jobPosting.eq_average >= 80 ? 'success' : jobPosting.eq_average >= 60 ? 'warning' : 'danger'}
                                      className="fs-6"
                                    >
                                      {jobPosting.eq_average.toFixed(1)}
                                    </Badge>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <div className="text-center">
                                  {jobPosting.value_average ? (
                                    <Badge 
                                      bg={jobPosting.value_average >= 80 ? 'success' : jobPosting.value_average >= 60 ? 'warning' : 'danger'}
                                      className="fs-6"
                                    >
                                      {jobPosting.value_average.toFixed(1)}
                                    </Badge>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </div>
                              </td>
                              <td>
                                <div className="d-flex align-items-center gap-2">
                                  <Badge bg={statusInfo.color}>{statusInfo.label}</Badge>
                                  {(jobPosting.status === '1' || jobPosting.status === '2') && (
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      onClick={() => handleToggleStatus(jobPosting.id, jobPosting.status)}
                                      disabled={isLoading}
                                      title={jobPosting.status === '1' ? 'Pause' : 'Activate'}
                                    >
                                      <i className={`ri-${jobPosting.status === '1' ? 'pause' : 'play'}-line`}></i>
                                    </Button>
                                  )}
                                </div>
                              </td>
                              <td>
                                <div className="d-flex gap-2">
                                  <Dropdown>
                                    <Dropdown.Toggle 
                                      variant="dark" 
                                      id={`action-dropdown-${jobPosting.id}`} 
                                      className="btn-sm"
                                      disabled={isLoading}
                                    >
                                      <i className="ri-more-2-fill"></i>
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu>
                                      <Dropdown.Item onClick={() => handleShowEditModal(jobPosting)}>
                                        <i className="ri-edit-line me-2"></i> Edit
                                      </Dropdown.Item>
                                      <Dropdown.Item onClick={() => handleDelete(jobPosting.id)}>
                                        <i className="ri-delete-bin-line me-2"></i> Delete
                                      </Dropdown.Item>
                                      {jobPosting.qr_code && (
                                        <Dropdown.Item 
                                          onClick={() => window.open(jobPosting.qr_code!, '_blank')}
                                        >
                                          <i className="ri-qr-code-line me-2"></i> View QR Code
                                        </Dropdown.Item>
                                      )}
                                    </Dropdown.Menu>
                                  </Dropdown>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={11} className="text-center">
                            No job postings available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Modal for Adding/Editing Job Posting */}
        <Modal show={showModal} onHide={handleCloseModal} backdrop="static" centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{editMode ? 'Edit Job Posting' : 'Add Job Posting'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label htmlFor="nama">Job Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      id="nama"
                      placeholder="Enter job name"
                      value={formData.nama}
                      onChange={handleInputChange}
                      disabled={isLoading}
                      isInvalid={!formData.nama.trim()}
                    />
                    <Form.Control.Feedback type="invalid">
                      Job name is required.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label htmlFor="flatform">Platform <span className="text-danger">*</span></Form.Label>
                    <div className="input-group">
                      <Form.Select
                        id="flatform"
                        value={formData.flatform}
                        onChange={handleInputChange}
                        disabled={isLoading}
                        isInvalid={!formData.flatform.trim()}
                        className="form-select"
                      >
                        <option value="">Select Platform</option>
                        {platformOptions.map((option) => (
                          <option key={option.id} value={option.flatform_name}>
                            {option.flatform_name}
                          </option>
                        ))}
                      </Form.Select>
                      <Button
                        variant="outline-primary"
                        onClick={handleAddPlatform}
                        disabled={isLoading}
                        title="Add New Platform"
                        className="border-start-0"
                      >
                        <i className="ri-add-line"></i>
                      </Button>
                    </div>
                    <Form.Control.Feedback type="invalid">
                      Platform is required.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>


            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal} disabled={isLoading}>
              Cancel
            </Button>
            <Button variant="dark" onClick={handleFormSubmit} disabled={isLoading}>
              {isLoading ? (editMode ? 'Updating...' : 'Saving...') : (editMode ? 'Update' : 'Save')}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal for Adding New Platform */}
        <Modal 
          show={showPlatformModal} 
          onHide={handleClosePlatformModal} 
          backdrop="static" 
          centered 
          size="lg"
        >
          <Modal.Header closeButton className="bg-primary text-white">
            <Modal.Title>
              <i className="ri-add-circle-line me-2"></i>
              Add New Platform
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <div className="text-center mb-4">
              <div 
                className="bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                style={{width: '80px', height: '80px'}}
              >
                <i className="ri-global-line text-primary" style={{fontSize: '2.5rem'}}></i>
              </div>
              <h5 className="mb-2">Create New Platform</h5>
              <p className="text-muted mb-0">
                Add a new job posting platform to expand your recruitment channels
              </p>
            </div>

            <Form>
              <Form.Group className="mb-4">
                <Form.Label htmlFor="newPlatformName" className="fw-semibold">
                  Platform Name <span className="text-danger">*</span>
                </Form.Label>
                <div className="input-group">
                  <span className="input-group-text bg-light">
                    <i className="ri-building-line text-muted"></i>
                  </span>
                  <Form.Control
                    type="text"
                    id="newPlatformName"
                    placeholder="e.g., LinkedIn, Indeed, JobStreet"
                    value={newPlatformName}
                    onChange={handlePlatformNameChange}
                    disabled={isLoading}
                    className="form-control-lg"
                    autoFocus
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSubmitNewPlatform();
                      }
                    }}
                  />
                </div>
                <Form.Text className="text-muted">
                  <i className="ri-information-line me-1"></i>
                  Platform names should be unique and descriptive
                </Form.Text>
              </Form.Group>

              {/* Real-time Validation Display */}
              {newPlatformName.trim() && (
                <div className="mb-3">
                  {(() => {
                    const error = validatePlatformName();
                    if (error) {
                      return (
                        <div className="alert alert-danger py-2 px-3 small mb-0">
                          <i className="ri-error-warning-line me-2"></i>
                          {error}
                        </div>
                      );
                    } else {
                      return (
                        <div className="alert alert-success py-2 px-3 small mb-0">
                          <i className="ri-check-double-line me-2"></i>
                          Platform name is valid and available
                        </div>
                      );
                    }
                  })()}
                </div>
              )}

              {/* Preview */}
              {newPlatformName.trim() && !validatePlatformName() && (
                <div className="mb-3">
                  <label className="form-label small text-muted">Preview:</label>
                  <div className="border rounded p-3 bg-light">
                    <Badge bg="info" className="fs-6">
                      {newPlatformName.trim()}
                    </Badge>
                    <small className="text-muted ms-2">
                      This is how it will appear in the platform list
                    </small>
                  </div>
                </div>
              )}
            </Form>
          </Modal.Body>
          <Modal.Footer className="bg-light">
            <Button 
              variant="light" 
              onClick={handleClosePlatformModal} 
              disabled={isLoading}
              className="me-2"
            >
              <i className="ri-close-line me-2"></i>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSubmitNewPlatform} 
              disabled={isLoading || !newPlatformName.trim() || !!validatePlatformName()}
              className="px-4"
            >
              {isLoading ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Adding...
                </>
              ) : (
                <>
                  <i className="ri-add-circle-line me-2"></i>
                  Add Platform
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </React.Fragment>
  );
};

export default JobPosting;
